import { useState, useEffect } from "react";
import axios from "axios";
import { Link2, FileText, CreditCard, Users, ClipboardList, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GeneralInfo() {
  const [c, setC] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => setC(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importantLinks = [
    {
      icon: ClipboardList,
      label: c.gmLink1Label || "Membership Form",
      url: c.gmLink1Url || "",
      desc: c.gmLink1Desc || "Complete this form to become an official Texas BHA member.",
    },
    {
      icon: CreditCard,
      label: c.gmLink2Label || "Dues Payment",
      url: c.gmLink2Url || "",
      desc: c.gmLink2Desc || "Pay your membership dues to activate your membership.",
    },
    {
      icon: Users,
      label: c.gmLink3Label || "FAM Form",
      url: c.gmLink3Url || "",
      desc: c.gmLink3Desc || "Submit your FAM (Family and Member) form.",
    },
    {
      icon: FileText,
      label: c.gmLink4Label || "Requirements",
      url: c.gmLink4Url || "",
      desc: c.gmLink4Desc || "View the requirements for active membership status.",
    },
    {
      icon: CreditCard,
      label: c.gmLink5Label || "Venmo",
      url: c.gmLink5Url || "",
      desc: c.gmLink5Desc || "Quick payment link for dues and event fees.",
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="general-info-page">
      {/* Header */}
      <section className="relative bg-[#0F172A] text-white py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white rounded-full blur-[120px]" />
        </div>
        <div className="container-main relative z-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Member Resources</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            General Member Info
          </h1>
          <p className="text-slate-300 mt-4 text-base max-w-2xl">
            {c.gmHeaderDesc || "Everything you need to know as a Texas BHA member — forms, payments, requirements, and more."}
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 lg:py-20 bg-white" data-testid="gm-overview">
        <div className="container-main max-w-3xl">
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {c.gmOverviewTitle || "Member Information"}
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">
            {c.gmOverviewText || "As a Texas BHA member, you have access to professional development events, committee involvement, networking opportunities, and more. Below you'll find all the important links and resources you need to stay active and engaged."}
          </p>
          <p className="text-slate-600 mt-3 leading-relaxed text-[15px]">
            {c.gmOverviewText2 || "If you have any questions, don't hesitate to reach out to our team."}
          </p>
        </div>
      </section>

      <Separator className="bg-slate-100" />

      {/* Important Links */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="gm-links">
        <div className="container-main max-w-3xl">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="h-5 w-5 text-[#0F172A]" />
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Quick Access</p>
          </div>
          <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {c.gmLinksTitle || "Important Links"}
          </h2>

          <div className="space-y-4">
            {importantLinks.map((link, i) => (
              <Card key={`gm-link-${i}`} data-testid={`gm-link-${i}`} className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0F172A] flex items-center justify-center shrink-0">
                      <link.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[15px] text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{link.label}</h3>
                      <p className="text-slate-500 text-sm mt-1 leading-relaxed">{link.desc}</p>
                    </div>
                    {link.url ? (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid={`gm-link-btn-${i}`}>
                        <Button size="sm" className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-5 text-xs font-semibold shrink-0">
                          Open <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                        </Button>
                      </a>
                    ) : (
                      <Button size="sm" className="bg-slate-200 text-slate-400 rounded-full px-5 text-xs font-semibold shrink-0 cursor-not-allowed" disabled>
                        Coming Soon
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-14 bg-[#0F172A] text-white text-center" data-testid="gm-cta">
        <div className="container-main">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{c.gmCTATitle || "Need Help?"}</h2>
          <p className="text-slate-400 mt-2 max-w-md mx-auto text-sm">{c.gmCTAText || "If you have questions about membership, payments, or requirements, reach out to us anytime."}</p>
        </div>
      </section>
    </div>
  );
}
